import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import type { MarkerClusterer } from "@googlemaps/markerclusterer";
import { UserMarkerInfoWindow } from "@locators/components/shared/components/UserMarkerInfoWindow";
import { DARK_MAP_STYLES_CONFIG, LIGHT_MAP_STYLES_CONFIG } from "@locators/components/shared/store-locator.config";
import debounce from "lodash.debounce";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlotiqStore } from "@jti/fe-common";
import { useMessageBus } from "@jti/fe-message-bus";

import { createStoreIconFactory, USER_LOCATION } from "../../store-locator-icons";
import type { CustomInfoOverlayInterface } from "../../utils/CustomGoogleInfoOverlay";
import createCustomInfoOverlay from "../../utils/CustomGoogleInfoOverlay";

import { createClusterer } from "./uitls";
import type { UseGoogleMapsStoreLocator } from "./useGoogleMapsStoreLocator.types";

const ENTER_KEY = 13;
const DOWN_ARROW_KEY = 40;
export const GOOGLE_DIRECTION_URL_CONST = "https://www.google.com/maps/dir/?api=1&destination=";

type NoStoresSearchState = { value: boolean; type: string } | null;

export const useGoogleMapsStoreLocator: UseGoogleMapsStoreLocator = ({
  defaultLat,
  defaultLng,
  defaultZoom,
  activeStoreId,
  handleSelectStore,
  handleLoadFlotiqStores,
  defaultStoreIcon,
  activeStoreIcon,
  storeIcons,
  isSearchVisible,
  onActivity,
  onTap,
  mapTheme,
}) => {
  const { ISO_COUNTRY_CODE, storeLocatorMapTheme = "dark", storeLocatorLayoutVersion: layoutVersion } = useConfig();

  const mapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const infoOverlayRef = useRef<CustomInfoOverlayInterface | null>(null);
  const markersRef = useRef<Record<string, google.maps.Marker>>({});
  const markerStoresRef = useRef<Record<string, FlotiqStore>>({});
  const isMarkerClickRef = useRef<boolean>(false);
  const activeMarkerRef = useRef<google.maps.Marker | null>(null);
  const tapTimeout = useRef<number | null>(null);

  const [markerCluster, setMarkerCluster] = useState<MarkerClusterer | null>(null);
  const [isMapLoading, setIsMapLoading] = useState<boolean>(false);
  const [isMapError, setIsMapError] = useState<boolean>(false);
  const [clusterClickTimestamp, setClusterClickTimestamp] = useState(0);
  const [isNoStoresSearch, setIsNoStoresSearch] = useState<NoStoresSearchState>(null);
  const messageBus = useMessageBus();

  const resolvedMapTheme = mapTheme ?? storeLocatorMapTheme;
  const isDarkTheme = resolvedMapTheme === "dark";
  const mapStyles = isDarkTheme ? DARK_MAP_STYLES_CONFIG : LIGHT_MAP_STYLES_CONFIG;

  const storeIconMap = useMemo(
    () =>
      (storeIcons ?? []).reduce(
        (map, { storeType, storeIcon }) => {
          if (storeType && storeIcon) {
            map[storeType] = storeIcon;
          }
          return map;
        },
        {} as Record<string, string>
      ),
    [storeIcons]
  );

  const getStoreIcon = useCallback(
    createStoreIconFactory({
      layoutVersion,
      theme: resolvedMapTheme,
      defaultStoreIcon,
      activeStoreIcon,
      storeTypeOverrides: storeIconMap,
    }),
    [defaultStoreIcon, activeStoreIcon, storeIconMap, layoutVersion, resolvedMapTheme]
  );

  const clustererFactory = createClusterer({ layoutVersion, theme: resolvedMapTheme });

  const throwConfigError = () => {
    setIsMapError(true);
    return "";
  };

  const waitForMapIdle = (callback: () => void) => {
    if (!googleMapRef.current) return;
    google.maps.event.addListenerOnce(googleMapRef.current, "idle", callback);
  };

  const options = useMemo(
    () => ({
      componentRestrictions: {
        country: ISO_COUNTRY_CODE || throwConfigError(),
      },
      fields: ["geometry", "name"],
      types: ["geocode", "establishment"],
    }),
    [ISO_COUNTRY_CODE]
  );

  const debouncedLoadStores = useCallback(
    debounce((map?: google.maps.Map | null) => {
      if (!isMarkerClickRef.current) {
        handleLoadFlotiqStores(map);
      }
    }, 300),
    [handleLoadFlotiqStores]
  );

  const handleLoadMarkers = (stores: FlotiqStore[]) => {
    if (!googleMapRef.current) {
      return;
    }

    if (stores.length > 0) {
      setIsNoStoresSearch(null);
    }

    const currentActiveMarkerId = activeMarkerRef.current?.get("storeData")?.id;
    const newMarkersMap: Record<string, google.maps.Marker> = {};
    const newMarkerStoresMap: Record<string, FlotiqStore> = {};

    if (markerCluster) {
      markerCluster.clearMarkers();
    }
    requestAnimationFrame(() => {
      stores.forEach((store) => {
        const { Location, id } = store;
        const markerIcon = getStoreIcon(store);
        if (!markerIcon?.default) {
          return;
        }
        const icon = { url: markerIcon.default };

        if (markersRef.current[id]) {
          const existingMarker = markersRef.current[id];
          newMarkerStoresMap[id] = store;
          existingMarker.set("storeData", store);
          existingMarker.set("defaultIcon", icon);
          existingMarker.setIcon(icon);

          const position = existingMarker.getPosition();
          if (position && (position.lat() !== Location.lat || position.lng() !== Location.lon)) {
            existingMarker.setPosition({
              lat: Location.lat,
              lng: Location.lon,
            });
          }

          newMarkersMap[id] = existingMarker;
          return existingMarker;
        }

        const marker = new google.maps.Marker({
          optimized: true,
          title: id,
          position: {
            lng: Location.lon,
            lat: Location.lat,
          },
          icon,
        });

        marker.set("defaultIcon", icon);
        marker.set("storeData", store);
        newMarkerStoresMap[id] = store;

        marker.addListener("click", () => {
          handleMarkerEvent(marker, true, store);
        });

        marker.addListener("dblclick", () => {
          handleMarkerEvent(marker, false, store);
        });

        newMarkersMap[id] = marker;
      });

      markersRef.current = newMarkersMap;
      markerStoresRef.current = newMarkerStoresMap;

      if (googleMapRef.current) {
        const currentMarkerCluster = clustererFactory(googleMapRef.current, Object.values(newMarkersMap));

        if (
          typeof google.maps.event.addListener === "function" &&
          currentMarkerCluster &&
          typeof currentMarkerCluster.addListener === "function"
        ) {
          currentMarkerCluster.addListener("click", () => {
            setClusterClickTimestamp(Date.now());
          });
        }

        setMarkerCluster(currentMarkerCluster);
      }

      if (currentActiveMarkerId && newMarkersMap[currentActiveMarkerId]) {
        const markerToActivate = newMarkersMap[currentActiveMarkerId];
        handleMarkerEvent(markerToActivate, false, newMarkerStoresMap[currentActiveMarkerId]);
      }
    });
  };

  const handleMarkerEvent = (marker: google.maps.Marker, shouldCenter: boolean, storeOverride?: FlotiqStore) => {
    isMarkerClickRef.current = true;

    if (activeMarkerRef.current && activeMarkerRef.current !== marker) {
      activeMarkerRef.current.setIcon(activeMarkerRef.current.get("defaultIcon"));
    }

    const store = storeOverride ?? (marker.get("storeData") as FlotiqStore | undefined);
    if (!store) {
      return;
    }

    const markerIcon = getStoreIcon(store);
    marker.setIcon({ url: markerIcon?.active ?? markerIcon?.default ?? "" });

    activeMarkerRef.current = marker;

    if (shouldCenter && googleMapRef.current) {
      debouncedLoadStores.cancel();

      const position = marker.getPosition();
      if (position) {
        googleMapRef.current.setCenter({
          lat: position.lat(),
          lng: position.lng(),
        });

        const wasRecentClusterClick = Date.now() - clusterClickTimestamp < 1000;

        if (!wasRecentClusterClick) {
          const currentZoom = googleMapRef.current.getZoom();

          if (typeof currentZoom === "number" && currentZoom < 15) {
            const bounds = new google.maps.LatLngBounds();

            const lat = position.lat();
            const lng = position.lng();
            const offset = 0.001;

            bounds.extend(new google.maps.LatLng(lat - offset, lng - offset));
            bounds.extend(new google.maps.LatLng(lat + offset, lng + offset));

            googleMapRef.current.fitBounds(bounds);
          }
        }
      }

      waitForMapIdle(() => {
        handleSelectStore(marker.getTitle() || "");

        if (googleMapRef.current) {
          handleLoadFlotiqStores(googleMapRef.current);
        }

        isMarkerClickRef.current = false;
      });
    } else {
      handleSelectStore(marker.getTitle() || "");

      waitForMapIdle(() => {
        isMarkerClickRef.current = false;
      });
    }
  };

  const handleInitMap = useCallback(() => {
    if (googleMapRef.current) {
      return;
    }

    setIsMapLoading(true);
    const pos = {
      lat: Number(defaultLat),
      lng: Number(defaultLng),
    };

    if (!mapRef.current) {
      setIsMapLoading(false);
      return;
    }

    const map = new google.maps.Map(mapRef.current, {
      center: { lat: pos.lat, lng: pos.lng },
      zoom: defaultZoom,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      styles: mapStyles,
    });
    const infoWindow = new google.maps.InfoWindow({
      content: "",
      disableAutoPan: true,
    });

    infoWindowRef.current = infoWindow;
    googleMapRef.current = map;

    infoOverlayRef.current = createCustomInfoOverlay(map);

    infoOverlayRef.current.addCloseHandler(() => {
      if (activeMarkerRef.current) {
        activeMarkerRef.current.setIcon(activeMarkerRef.current.get("defaultIcon"));
        activeMarkerRef.current = null;
      }
      handleSelectStore("");
    });

    google.maps.event.addListenerOnce(map, "bounds_changed", () => {
      handleLoadFlotiqStores(googleMapRef.current);
    });

    infoWindow.addListener("closeclick", () => {
      if (activeMarkerRef.current) {
        activeMarkerRef.current.setIcon(activeMarkerRef.current.get("defaultIcon"));
        activeMarkerRef.current = null;
      }
      handleSelectStore("");
    });

    const handleTap = () => {
      if (tapTimeout.current) {
        clearTimeout(tapTimeout.current);
      }
      tapTimeout.current = window.setTimeout(() => {
        onTap?.();
        tapTimeout.current = null;
      }, 180);
    };

    const cancelTap = () => {
      if (tapTimeout.current) {
        clearTimeout(tapTimeout.current);
        tapTimeout.current = null;
      }
      onActivity?.();
    };

    const tapListener = map.addListener("click", handleTap);
    const gestureEvents = ["dragstart", "zoom_changed", "wheel", "touchmove"];
    const gestureListeners = gestureEvents.map((evt) => map.addListener(evt, cancelTap));

    const idleListener = map.addListener("idle", () => {
      debouncedLoadStores(googleMapRef.current);
    });

    setIsMapLoading(false);

    return () => {
      idleListener.remove();
      tapListener.remove();
      gestureListeners.forEach((l) => l.remove());
      if (tapTimeout.current) clearTimeout(tapTimeout.current);
    };
  }, [defaultLat, defaultLng, defaultZoom, handleSelectStore, debouncedLoadStores, mapStyles]);

  useEffect(() => {
    if (!googleMapRef.current) {
      return;
    }

    googleMapRef.current.setOptions({ styles: mapStyles });
  }, [mapStyles]);

  const handleLoadUserMarker = (userLat: number, userLng: number) => {
    if (!googleMapRef.current) {
      return;
    }
    const image = {
      url: USER_LOCATION,
      scaledSize: new google.maps.Size(30, 30),
    };

    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<UserMarkerInfoWindow />);

    const userLocationInfoWindow = new google.maps.InfoWindow({
      content: container,
      disableAutoPan: true,
    });

    const userLocationMarker = new google.maps.Marker({
      position: {
        lat: userLat,
        lng: userLng,
      },
      icon: image,
      map: googleMapRef.current,
    });

    userLocationMarker.addListener("click", () => {
      if (googleMapRef.current) {
        userLocationInfoWindow.open(googleMapRef.current, userLocationMarker);
      }
    });

    isMarkerClickRef.current = true;
    googleMapRef.current.setCenter({
      lat: userLat,
      lng: userLng,
    });

    waitForMapIdle(async () => {
      isMarkerClickRef.current = false;
      await handleLoadFlotiqStores(googleMapRef.current);
      setIsNoStoresSearch({ value: true, type: "location" });
    });
  };

  const handleSearchForm = useCallback((place: google.maps.places.PlaceResult) => {
    messageBus.publish("tracking.event", {
      payload: {
        event_name: "store_locator_search",
        additionalData: {
          search_term: inputRef.current?.value ?? "",
        },
      },
    });
    setIsNoStoresSearch({ value: true, type: "search" });

    if (place?.geometry) {
      isMarkerClickRef.current = true;

      if (place.geometry.viewport) {
        googleMapRef?.current?.fitBounds(place.geometry.viewport);
      } else {
        googleMapRef?.current?.setCenter(place.geometry.location);
      }

      inputRef.current?.blur();

      waitForMapIdle(() => {
        isMarkerClickRef.current = false;
        handleLoadFlotiqStores(googleMapRef.current);
      });
    }
  }, []);

  const handleInputSearchClick = () => {
    const noop = () => null;

    if (!inputRef.current) {
      return;
    }

    google.maps.event.trigger(inputRef.current, "focus", {});
    google.maps.event.trigger(inputRef.current, "keydown", {
      keyCode: DOWN_ARROW_KEY,
      stopPropagation: noop,
      preventDefault: noop,
    });
    google.maps.event.trigger(inputRef.current, "keydown", { keyCode: ENTER_KEY });
    google.maps.event.trigger(googleMapRef.current, "zoom_changed", {});
  };

  const onPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) {
      return;
    }
    handleSearchForm(place);
  }, []);

  const handleAddCustomEventListenerWrapper = () => {
    if (inputRef.current) {
      const _addEventListener = inputRef.current.addEventListener;

      const addEventListenerWrapper = (type: string, listener: any) => {
        if (type === "keydown") {
          const originalListener = listener;
          listener = function (event: KeyboardEvent) {
            const suggestionSelected = document.querySelector(".pac-item-selected");
            if (event.which === ENTER_KEY && !suggestionSelected) {
              const simulatedEvent = new KeyboardEvent("keydown", {
                keyCode: DOWN_ARROW_KEY,
                which: DOWN_ARROW_KEY,
              });
              originalListener.apply(inputRef.current, [simulatedEvent]);
            }
            originalListener.apply(inputRef.current, [event]);
          };
        }
        _addEventListener.apply(inputRef.current, [type, listener as EventListenerOrEventListenerObject]);
      };

      inputRef.current.addEventListener = addEventListenerWrapper;
    }
  };

  const handleMountAutoComplete = useCallback(() => {
    if (inputRef.current && googleMapRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      autocompleteRef.current.bindTo("bounds", googleMapRef.current);
      autocompleteRef.current.addListener("place_changed", onPlaceChanged);
      handleAddCustomEventListenerWrapper();
    }
  }, [onPlaceChanged, options]);

  const handleSelectStoreFromList = (storeId: string) => {
    if (!storeId || !googleMapRef.current) return;
    const activeMarker = markersRef.current[storeId];

    if (activeMarker) {
      debouncedLoadStores.cancel();

      handleMarkerEvent(activeMarker, true, markerStoresRef.current[storeId]);
    } else {
      handleSelectStore(storeId);
    }
  };

  useEffect(() => {
    handleMountAutoComplete();
  });

  useEffect(() => {
    if (activeStoreId) {
      const activeMarker = markersRef.current[activeStoreId];
      if (activeMarker && google) {
        handleMarkerEvent(activeMarker, false, markerStoresRef.current[activeStoreId]);
      } else {
        handleSelectStore("");
        infoWindowRef.current?.close();
        infoOverlayRef.current?.close();
      }
    }
  }, [activeStoreId]);

  useEffect(() => {
    return () => {
      if (infoOverlayRef.current) {
        infoOverlayRef.current.setMap(null);
      }
      debouncedLoadStores.cancel();
    };
  }, [debouncedLoadStores]);

  const handleMapExpansion = () => {
    if (!googleMapRef.current) {
      return;
    }

    const currentZoom = googleMapRef.current.getZoom();
    const mapExpansionZoom = 7;
    const zoom =
      currentZoom && currentZoom > mapExpansionZoom ? mapExpansionZoom : (currentZoom ?? mapExpansionZoom) - 1;
    googleMapRef.current.setZoom(zoom);
  };

  return {
    mapRef,
    isSearchVisible,
    inputRef,
    isMapError,
    isMapLoading,
    handleLoadUserMarker,
    handleLoadMarkers,
    handleInitMap,
    handleInputSearchClick,
    handleSelectStoreFromList,
    isNoStoresSearch,
    handleMapExpansion,
  };
};
